import React, { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { toast } from "sonner";
import { History, RotateCcw, GitCompareArrows, Loader2, Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";

const KIND_STYLE = {
  publish: "bg-[color:rgba(0,194,168,0.15)] text-[color:var(--brand-mint)]",
  manual: "bg-[color:rgba(43,78,255,0.12)] text-[color:var(--brand-ocean)]",
  autosave: "bg-[color:var(--surface-2)] text-[color:var(--ink-3)]",
  "pre-restore": "bg-[color:rgba(226,75,91,0.10)] text-[color:var(--danger)]",
};

function diffWidgets(a = [], b = []) {
  const A = Object.fromEntries(a.map((w) => [w.id, w]));
  const B = Object.fromEntries(b.map((w) => [w.id, w]));
  const label = (w) => w.title || w.type || "widget";
  const added = b.filter((w) => !A[w.id]).map(label);
  const removed = a.filter((w) => !B[w.id]).map(label);
  const changed = b.filter((w) => A[w.id] && JSON.stringify(A[w.id]) !== JSON.stringify(w)).map(label);
  return { added, removed, changed };
}

export function VersionHistoryDialog({ open, onOpenChange, dashboardId, onRestored }) {
  const [versions, setVersions] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [compareSel, setCompareSel] = useState([]);   // up to 2 version ids
  const [diff, setDiff] = useState(null);

  const load = useCallback(() => api.get(`/dashboards/${dashboardId}/versions`).then(({ data }) => setVersions(data)).catch(() => setVersions([])), [dashboardId]);
  useEffect(() => { if (open) { setCompareSel([]); setDiff(null); load(); } }, [open, load]);

  const saveVersion = async () => {
    setBusy(true);
    try { await api.post(`/dashboards/${dashboardId}/versions`, { note }); setNote(""); toast.success("Version saved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to save version"); }
    finally { setBusy(false); }
  };

  const restore = async (v) => {
    if (!window.confirm(`Restore the version from ${new Date(v.created_at).toLocaleString()}? A safety checkpoint of the current state is saved first.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/dashboards/${dashboardId}/versions/${v.id}/restore`);
      toast.success("Version restored");
      onRestored && onRestored(data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
    finally { setBusy(false); }
  };

  const toggleCompare = (vid) => {
    setDiff(null);
    setCompareSel((s) => s.includes(vid) ? s.filter((i) => i !== vid) : [...s.slice(-1), vid]);
  };
  const runCompare = async () => {
    if (compareSel.length !== 2) return;
    setBusy(true);
    try {
      const [va, vb] = await Promise.all(compareSel.map((id) => api.get(`/dashboards/${dashboardId}/versions/${id}`)));
      const [oldV, newV] = [va.data, vb.data].sort((x, y) => x.created_at.localeCompare(y.created_at));
      setDiff({ oldV, newV, ...diffWidgets(oldV.snapshot?.widgets, newV.snapshot?.widgets) });
    } catch { toast.error("Compare failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="version-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><History className="w-5 h-5 text-[color:var(--brand-ocean)]" />Version history</DialogTitle>
        </DialogHeader>

        <div className="flex gap-2">
          <Input data-testid="version-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional change note…" className="bg-[color:var(--surface)]" />
          <Button data-testid="version-save-btn" onClick={saveVersion} disabled={busy} className="bg-[color:var(--brand-ocean)] text-white shrink-0"><Plus className="w-4 h-4 mr-1" />Save version</Button>
        </div>

        {compareSel.length > 0 && (
          <div className="flex items-center gap-2 text-xs text-[color:var(--ink-3)]">
            <GitCompareArrows className="w-3.5 h-3.5" />
            {compareSel.length === 1 ? "Pick one more version to compare" : (
              <Button data-testid="version-compare-btn" size="sm" variant="outline" className="h-7" onClick={runCompare} disabled={busy}>Compare selected</Button>
            )}
            <button onClick={() => { setCompareSel([]); setDiff(null); }} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}

        {diff && (
          <div data-testid="version-diff" className="rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3 text-xs space-y-1.5">
            <div className="font-semibold text-[color:var(--ink)]">
              {new Date(diff.oldV.created_at).toLocaleString()} → {new Date(diff.newV.created_at).toLocaleString()}
            </div>
            {diff.added.length === 0 && diff.removed.length === 0 && diff.changed.length === 0 && <div className="text-[color:var(--ink-3)]">No widget differences.</div>}
            {diff.added.length > 0 && <div><span className="text-[color:var(--brand-mint)] font-medium">Added:</span> {diff.added.join(", ")}</div>}
            {diff.removed.length > 0 && <div><span className="text-[color:var(--danger)] font-medium">Removed:</span> {diff.removed.join(", ")}</div>}
            {diff.changed.length > 0 && <div><span className="text-[color:var(--brand-ocean)] font-medium">Changed:</span> {diff.changed.join(", ")}</div>}
          </div>
        )}

        <div className="space-y-1.5">
          {versions === null && <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[color:var(--brand-ocean)]" /></div>}
          {versions?.length === 0 && <div className="py-6 text-center text-xs text-[color:var(--ink-3)]">No versions yet — they're created automatically while you edit and when you publish.</div>}
          {(versions || []).map((v) => (
            <div key={v.id} data-testid="version-row" className={cn("flex items-center gap-2 px-3 py-2 rounded-xl border transition-colors",
              compareSel.includes(v.id) ? "border-[color:var(--brand-ocean)] bg-[color:rgba(43,78,255,0.06)]" : "border-[color:var(--glass-border)]")}>
              <input type="checkbox" data-testid="version-compare-check" checked={compareSel.includes(v.id)} onChange={() => toggleCompare(v.id)} className="accent-[color:var(--brand-ocean)]" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-medium text-[color:var(--ink)]">{new Date(v.created_at).toLocaleString()}</span>
                  <Badge className={cn("border-0 text-[10px] px-1.5", KIND_STYLE[v.kind] || KIND_STYLE.autosave)}>{v.kind}</Badge>
                  <span className="text-[10px] text-[color:var(--ink-3)]">{v.widget_count} widgets</span>
                </div>
                <div className="text-[11px] text-[color:var(--ink-3)] truncate">{v.note || v.created_by}</div>
              </div>
              <Button data-testid="version-restore-btn" size="sm" variant="ghost" className="h-7 text-[color:var(--brand-ocean)]" disabled={busy} onClick={() => restore(v)}>
                <RotateCcw className="w-3.5 h-3.5 mr-1" />Restore
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
